import {apiRequest} from './client';
import type {
    BillingInfo,
    DowngradeValidation,
    Invoice,
    PricingData,
    ProrationData,
    SiteUsageData,
    SiteUsageDetail,
    Subscription
} from '@/types/api';

export async function getBillingInfo(): Promise<BillingInfo[]> {
    return apiRequest<BillingInfo[]>('/billing', {
        method: 'GET'
    });
}

export async function getSubscription(siteId: string): Promise<Subscription | null> {
    return apiRequest<Subscription | null>(`/billing/subscription?siteId=${siteId}`, {
        method: 'GET'
    });
}

export async function getUsage(): Promise<SiteUsageData[]> {
    return apiRequest<SiteUsageData[]>('/billing/usage', {
        method: 'GET'
    });
}

export async function getSiteUsage(siteId: string): Promise<SiteUsageDetail> {
    return apiRequest<SiteUsageDetail>(`/billing/usage/${siteId}`, {
        method: 'GET'
    });
}

export async function initiateUpgrade(
    planId: string,
    billingPeriod: 'monthly' | 'yearly' | 'lifetime' = 'monthly'
): Promise<{ checkoutUrl: string | null; billingPeriod: string; plan: { id: string; name: string } }> {
    return apiRequest<{ checkoutUrl: string | null; billingPeriod: string; plan: { id: string; name: string } }>(
        '/billing/upgrade',
        {
            method: 'POST',
            body: JSON.stringify({planId, billingPeriod})
        }
    );
}

export async function validateDowngrade(
    planId: string
): Promise<DowngradeValidation> {
    return apiRequest<DowngradeValidation>('/billing/downgrade', {
        method: 'POST',
        body: JSON.stringify({planId})
    });
}

export async function scheduleDowngrade(
    planId: string
): Promise<{ scheduledAt: string; newPlanName: string }> {
    return apiRequest<{ scheduledAt: string; newPlanName: string }>(
        '/billing/downgrade/schedule',
        {
            method: 'POST',
            body: JSON.stringify({planId})
        }
    );
}

export async function cancelScheduledDowngrade(): Promise<{ cancelled: boolean }> {
    return apiRequest<{ cancelled: boolean }>(
        '/billing/downgrade/schedule',
        {
            method: 'DELETE'
        }
    );
}

export async function getInvoices(
    limit: number = 20,
    offset: number = 0,
    siteId?: string
): Promise<{ invoices: Invoice[]; total: number }> {
    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset)
    });
    if (siteId) {
        params.append('siteId', siteId);
    }
    return apiRequest<{ invoices: Invoice[]; total: number }>(
        `/billing/invoices?${params.toString()}`,
        {
            method: 'GET'
        }
    );
}

export async function getInvoice(invoiceId: string): Promise<Invoice> {
    return apiRequest<Invoice>(`/billing/invoices/${invoiceId}`, {
        method: 'GET'
    });
}

export async function downloadInvoice(invoiceId: string): Promise<Blob> {
    // Session travels as a same-origin cookie, which fetch sends by default.
    const response = await fetch(`/api/billing/invoices/${invoiceId}/download`, {
        method: 'GET'
    });
    if (!response.ok) {
        throw new Error('Failed to download invoice');
    }
    return response.blob();
}

export async function getPricing(): Promise<PricingData> {
    return apiRequest<PricingData>('/pricing', {
        method: 'GET'
    });
}

export async function getProration(
    siteId: string,
    planId: string,
    billingPeriod: string = 'monthly'
): Promise<ProrationData> {
    return apiRequest<ProrationData>(
        `/billing/proration/${siteId}/${planId}?billingPeriod=${billingPeriod}`,
        {
            method: 'GET'
        }
    );
}
